import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import Loading from '../components/Loading';
import { get } from '../lib/Api';
import Linkify from '~/components/Linkify';
import OptionToggle from '~/components/OptionToggle.jsx';
import usePostApi from '~/lib/usePostApi';
import TextInput from '~/components/TextInput';
import * as Toast from '~/lib/Toast';
import { ToastContainer } from 'react-toastify';

const Row = ({ children, className }) => {
    return (
        <div className={`mb-4 p-4 bg-white max-w-md rounded-lg ${className}`}>
            {children}
        </div>
    );
};

const UserRow = ({ user }) => {
    const { save, isSaving } = usePostApi(`/api/admin/user/${user.id}`);
    const [isAgency, setIsAgency] = useState(user.is_agency);
    const [isSpam, setIsSpam] = useState(!!user?.settings?.spam);
    const [referralSlug, setReferralSlug] = useState(
        user.settings?.referral_slug,
    );
    // Blank falls back to the default for this account — 3, or 10 for an
    //  agency. Only an explicit number overrides it, zero included
    const [publicThemeLimit, setPublicThemeLimit] = useState(
        user.settings?.public_theme_limit ?? '',
    );

    const authMethod = user.auth0_id.split('|')[1];

    return (
        <Row>
            <div className="mb-2">
                {user.name} - {user.email}{' '}
                <span className="text-gray-500">[{authMethod}]</span>
            </div>
            <OptionToggle
                toggleFirst={true}
                title="Is Agency"
                className="mb-2"
                value={isAgency}
                onChange={value => {
                    setIsAgency(value);
                }}
            />
            <OptionToggle
                toggleFirst={true}
                title="Is Spammer"
                className="mb-2"
                value={isSpam}
                onChange={value => {
                    setIsSpam(value);
                }}
            />
            <TextInput
                value={referralSlug}
                onChange={value => {
                    setReferralSlug(value);
                }}
                label="Referral Slug"
                className="mb-2"
            />
            <TextInput
                type="number"
                min={0}
                value={publicThemeLimit}
                onChange={value => {
                    setPublicThemeLimit(value);
                }}
                label="Public Theme Limit"
                placeholder={`Default (${user.public_theme_limit})`}
                className="mb-1"
            />
            <div className="text-gray-500 text-sm mb-2">
                How many themes this user can publish publicly. Leave blank for
                the default — 3, or 10 for an agency. 0 stops them publishing.
            </div>
            <button
                type="button"
                className="btn-pill"
                onClick={() => {
                    save({
                        is_agency: isAgency,
                        referral_slug: referralSlug,
                        spam: isSpam,
                        public_theme_limit: publicThemeLimit,
                    }).finally(() => {
                        Toast.success('Successfully saved');
                    });
                }}
            >
                {isSaving ? 'Saving...' : 'Save'}
            </button>
        </Row>
    );
};

const BusinessRow = ({ business }) => {
    const [isSpam, setIsSpam] = useState(!!business.is_spam);
    const [isHipaa, setIsHipaa] = useState(!!business.is_hipaa);
    const [isFreeDisabled, setIsFreeDisabled] = useState(
        !!business.is_free_disabled,
    );
    const [isDirty, setIsDirty] = useState(false);

    const { save, isSaving } = usePostApi(`/api/admin/business/${business.id}`);

    return (
        <Row className="flex items-center justify-between">
            <div>
                <div>
                    [{business.id}] {business.name}
                </div>
                <div className="text-gray-500">{business.subdomain}</div>
                {isSpam && <div className="text-red-600">Spammer!</div>}
                <OptionToggle
                    toggleFirst={true}
                    title="Is Spammer"
                    className="mb-2"
                    value={isSpam}
                    onChange={value => {
                        setIsDirty(true);
                        setIsSpam(value);
                    }}
                />
                <OptionToggle
                    toggleFirst={true}
                    title="Is HIPAA"
                    className="mb-2"
                    value={isHipaa}
                    onChange={value => {
                        setIsDirty(true);
                        setIsHipaa(value);
                    }}
                />
                <OptionToggle
                    toggleFirst={true}
                    title="Enforce Free Plan Limits"
                    className="mb-2"
                    value={isFreeDisabled}
                    onChange={value => {
                        setIsDirty(true);
                        setIsFreeDisabled(value);
                    }}
                />
                {isDirty && (
                    <button
                        type="button"
                        className="btn-pill"
                        onClick={() => {
                            save({
                                spam: isSpam,
                                hipaa: isHipaa,
                                free_disabled: isFreeDisabled,
                            })
                                .then(() => {
                                    Toast.success('Successfully saved');
                                })
                                .finally(() => {
                                    setIsDirty(false);
                                });
                        }}
                    >
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                )}
            </div>
            <a
                href={business.impersonate}
                target="_blank"
                className="btn-light-pill"
            >
                Log in
            </a>
        </Row>
    );
};

const PayoutRow = ({ payout, isFirst }) => {
    return (
        <div
            className={`flex items-center justify-between py-3 pr-4 ${!isFirst ? 'border-t' : ''}`}
        >
            <div>
                <div>{payout.name || payout.slug}</div>
                {!!payout.name && (
                    <div className="text-gray-500">{payout.slug}</div>
                )}
            </div>
            <div>${Number(payout.amount).toFixed(2)}</div>
        </div>
    );
};

const AdminPage = ({}) => {
    const navigate = useNavigate();
    const { action, id } = useParams();
    const [results, setResults] = useState(null);

    useEffect(() => {
        get(`/api/admin/${action}/${id}`).then(result => {
            if (result.redirect_to) {
                navigate(result.redirect_to);
            } else {
                setResults(result);
            }
        });
    }, []);

    return (
        <div className="p-6 bg-gray-100">
            <ToastContainer
                position="top-center"
                autoClose={5000}
                hideProgressBar
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />

            {!results && <Loading />}
            {Array.isArray(results?.users) && (
                <div>
                    <h1>Users</h1>
                    {results?.users.map((result, idx) => (
                        <UserRow key={idx} user={result} />
                    ))}
                    {!results?.users?.length && (
                        <div className="text-gray-500">No results</div>
                    )}
                </div>
            )}
            {Array.isArray(results?.businesses) && (
                <div>
                    <h1>Businesses</h1>
                    {results?.businesses.map((result, idx) => (
                        <BusinessRow key={idx} business={result} />
                    ))}
                    {!results?.businesses?.length && (
                        <div className="text-gray-500">No results</div>
                    )}
                </div>
            )}
            {Array.isArray(results?.payouts) && (
                <div>
                    <h1>Revenue Share Payouts</h1>
                    <div className="panel pl-4 max-w-md">
                        {results.payouts.map((payout, idx) => (
                            <PayoutRow
                                key={payout.slug}
                                payout={payout}
                                isFirst={idx === 0}
                            />
                        ))}
                        {!results.payouts.length && (
                            <div className="text-gray-500 py-3">No results</div>
                        )}
                    </div>
                </div>
            )}
            {!!results &&
                !Array.isArray(results?.businesses) &&
                !Array.isArray(results?.payouts) && (
                    <pre>
                        <Linkify>{JSON.stringify(results, null, 2)}</Linkify>
                    </pre>
                )}
        </div>
    );
};

export default AdminPage;
